"use client";
import React from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination, Autoplay } from "swiper/modules";
// Import Swiper styles
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import "swiper/css/scrollbar";
import ArticleCard from "../../shared/ArticleCard";
import MyLink from "@/components/shared/myLink/MyLink";
import { useTranslate } from "@/components/base/providers/TranslateProv";
import { useGetArticlesQuery } from "@/redux/features/api/home/homeApiSlice";
import Loader from "@/components/shared/Loader";
import { Blog } from "@/types/blog";

const Index = ({ initialArticles = [] }: { initialArticles?: Blog[] }) => {
  const { locale } = useTranslate();
  const lang: "ar" | "en" = locale as "ar" | "en";

  const { data, isLoading, error } = useGetArticlesQuery({
    lang,
  });

  const articles = data?.data ?? initialArticles;
  const showLoader = isLoading && articles.length === 0;
  const showError = Boolean(error) && articles.length === 0;

  const translations = {
    ar: {
      sectionTitle: "المقالات",
      header: "قرائة افضل المدونات لدينا",
      description:
        "نافذتك الشاملة على أخبار الجامعات والتعليم العالي، مع مقالات وإرشادات موثوقة من سفير الجامعات.",
      viewAll: "عرض الكل",
    },
    en: {
      sectionTitle: "Articles",
      header: "Read Our Best Blogs",
      description:
        "Your hub for university and higher education news in Egypt",
      viewAll: "View All",
    },
  };

  const t = translations[lang];

  return (
    <div className="bg-white">
      <div className="container py-[100px]">
        <p className="text-20 text-primaryColor w-fit mx-auto py-1 px-7 border border-primaryColor rounded-full mb-2">
          {t.sectionTitle}
        </p>
        <h2 className="text-25 text-darkText font-[600] text-center mb-5">
          {t.header}
        </h2>
        <p className="text-20 text-[#6C7278] text-center max-w-[723px] mx-auto mb-10">
          {t.description}
        </p>

        {showLoader && (
          <div className="flex justify-center items-center h-[300px]">
            <Loader />
          </div>
        )}
        {showError && (
          <div className="flex justify-center items-center h-[300px]">
            <p className="text-center text-20 text-red-500 font-[600]">
              {lang === "ar" ? "خطأ في جلب البيانات." : "Error fetching data."}
            </p>
          </div>
        )}
        {!showLoader && !showError && (
          <Swiper
            modules={[Navigation, Pagination, Autoplay]}
            spaceBetween={10}
            slidesPerView={"auto"}
            breakpoints={{
              370: {
                slidesPerView: 1,
                spaceBetween: 10,
              },
              768: {
                slidesPerView: 2,
                spaceBetween: 10,
              },
              1280: {
                slidesPerView: 3,
                spaceBetween: 10,
              },
            }}
            autoplay={{ delay: 2000 }}
            pagination={{
              clickable: true,
              renderBullet(index, className) {
                return `<span class="${className} !rounded-[10px] !top-2 !bg-primaryColor !w-[45px] !h-[6px] transition"></span>`;
              },
            }}
            dir={locale === "ar" ? "rtl" : "ltr"}
            className="!pb-16"
          >
            {articles.map((blog: Blog) => (
              <SwiperSlide
                key={blog.id}
                className="!h-auto"
              >
                <ArticleCard blog={blog} />
              </SwiperSlide>
            ))}
          </Swiper>
        )}
        <MyLink
          href={"/articles"}
          className="text-16 text-primaryColor w-fit mx-auto py-2 px-7 rounded-[10px] border border-primaryColor block mt-5"
        >
          {t.viewAll}
        </MyLink>
      </div>
    </div>
  );
};

export default Index;
